// The pieces of the search form, as the design «Pagina Ricerca» (Figma, Versione 2,
// frame 159:2983) draws them, built from HeroUI: the tags of the choices under the
// field, a menu of the settings (Select).
import { Calendar, DateField, DatePicker, Description, I18nProvider, Label, ListBox, Select } from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { Link } from "react-router";

import { Icon } from "../../components/Icon";
import type { ChoiceTag } from "./adapt";
import type { SearchParams } from "./params";

/** The tags of the choices («Ordina più recenti ×»), and the «?» of the guide at the end of the row. */
export function ChoiceTags({
  tags,
  onReset,
  formulaOpen,
  onFormula,
}: {
  tags: ChoiceTag[];
  onReset: (reset: Partial<SearchParams>) => void;
  /** «Formula»: whether the box of the formula (how to write one, and this search as one) is open. */
  formulaOpen: boolean;
  onFormula: () => void;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <ul aria-label="Scelte della ricerca" className="flex flex-1 flex-wrap items-center gap-1.5">
        {tags.map((tag) => (
          <li
            key={tag.key}
            className="flex h-[22px] items-center gap-1 rounded-2xl bg-white px-2 py-px text-xs leading-5 font-medium whitespace-nowrap text-field-foreground"
          >
            {tag.label}
            {tag.reset && (
              <button
                type="button"
                aria-label={`Togli «${tag.label}»`}
                onClick={() => onReset(tag.reset!)}
                className="cursor-pointer rounded-sm"
              >
                <Icon name="xmark" />
              </button>
            )}
          </li>
        ))}
      </ul>
      <button
        type="button"
        aria-expanded={formulaOpen}
        onClick={onFormula}
        className="flex h-5 cursor-pointer items-center gap-1 rounded-sm text-xs leading-5 font-medium whitespace-nowrap text-accent underline-offset-2 hover:underline"
      >
        Formula
        <Icon name="chevronDown" className={`size-3 transition-transform ${formulaOpen ? "-scale-y-100" : ""}`} />
      </button>
      <Link to="/guida" aria-label="Guida alla ricerca" className="mt-0.5 rounded-full">
        <Icon name="circleQuestion" />
      </Link>
    </div>
  );
}

/** A choice of a menu; its note says what it means, under its name in the open menu. */
export type Option<T extends string> = { value: T; label: string; note?: string };

/** A preference as a menu: a white box of 192px with the choice in accent and the chevron (Accordion of the design). */
export function PreferenceSelect<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly Option<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <Select
      aria-label={label}
      selectedKey={value}
      onSelectionChange={(key) => {
        const next = options.find((o) => o.value === key);
        if (next) onChange(next.value);
      }}
      className="w-48"
    >
      <Select.Trigger className="flex h-14 w-full items-center gap-3 rounded-lg bg-white px-4 text-left shadow-field">
        <Select.Value className="flex-1 truncate text-sm leading-6 font-medium text-accent">
          {/* the name only: the note is for the open menu */}
          {({ selectedText }) => selectedText}
        </Select.Value>
        <Select.Indicator className="size-4 text-accent" />
      </Select.Trigger>
      <Select.Popover className="max-w-80 min-w-48 rounded-xl">
        <ListBox aria-label={label}>
          {options.map((o) => (
            <ListBox.Item
              key={o.value}
              id={o.value}
              textValue={o.label}
              aria-label={o.label}
              className="rounded-[20px] px-3 py-1.5"
            >
              <div className="flex flex-col">
                <Label className="text-sm leading-6 font-medium text-accent">{o.label}</Label>
                {o.note && <Description className="text-xs leading-4 font-normal text-muted">{o.note}</Description>}
              </div>
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}

/**
 * A day of «Da… a…»: the HeroUI date field and its calendar, in Italian whatever the browser's
 * language (Davide, 3/11): gg/mm/aaaa, the week from Monday, the months by their Italian names.
 */
export function DayField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: string | null;
  onChange: (day: string | null) => void;
  min?: string;
  max?: string;
}) {
  return (
    <I18nProvider locale="it-IT">
      <DatePicker
        value={value ? parseDate(value) : null}
        onChange={(day) => onChange(day ? day.toString() : null)}
        minValue={min ? parseDate(min) : undefined}
        maxValue={max ? parseDate(max) : undefined}
        className="flex w-48 flex-col gap-1"
      >
        <Label className="text-xs leading-5 font-medium text-muted">{label}</Label>
        <DateField.Group className="h-9 rounded-xl bg-field-background px-3 shadow-field">
          <DateField.Input className="text-sm leading-5 text-accent">
            {(segment) => <DateField.Segment segment={segment} />}
          </DateField.Input>
          <DateField.Suffix>
            <DatePicker.Trigger aria-label={`Apri il calendario: ${label}`}>
              <DatePicker.TriggerIndicator />
            </DatePicker.Trigger>
          </DateField.Suffix>
        </DateField.Group>
        <DatePicker.Popover>
          <Calendar aria-label={label}>
            <Calendar.Header>
              <Calendar.NavButton slot="previous" />
              <Calendar.Heading />
              <Calendar.NavButton slot="next" />
            </Calendar.Header>
            <Calendar.Grid>
              <Calendar.GridHeader>{(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}</Calendar.GridHeader>
              <Calendar.GridBody>{(date) => <Calendar.Cell date={date} />}</Calendar.GridBody>
            </Calendar.Grid>
          </Calendar>
        </DatePicker.Popover>
      </DatePicker>
    </I18nProvider>
  );
}
